import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ApartmentIcon from "@mui/icons-material/ApartmentOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import DemoStudioIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RoomTypeIcon from "@mui/icons-material/HotelOutlined";
import RadioIcon from "@mui/icons-material/Radio";
import AppsIcon from "@mui/icons-material/Apps";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import TranslateIcon from "@mui/icons-material/TranslateOutlined";
import ReviewIcon from "@mui/icons-material/FactCheckOutlined";
import SupplierIcon from "@mui/icons-material/HandshakeOutlined";
import InsightsIcon from "@mui/icons-material/InsightsOutlined";
import ContentAnalyticsIcon from "@mui/icons-material/QueryStatsOutlined";
import TemplatesIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import ScreenGroupsIcon from "@mui/icons-material/DevicesOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import PremiseOverviewIcon from "@mui/icons-material/SpaceDashboardOutlined";
import DevicesIcon from "@mui/icons-material/ConnectedTvOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import EnergyIcon from "@mui/icons-material/BoltOutlined";
import PhoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import VideoCallIcon from "@mui/icons-material/VideoCallOutlined";
import CampaignIcon from "@mui/icons-material/CampaignOutlined";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
// A tray with a bell: "order to the room · book the spa · skip the desk" is
// exactly what this section is, and it shares no glyph with Signage's megaphone.
// An inbox, because that is the noun: things arrive in it and somebody works
// them off. Distinct from the tray-and-bell above, which is the catalogue.
import OffersIcon from "@mui/icons-material/LocalOfferOutlined";
import RequestsIcon from "@mui/icons-material/InboxOutlined";
import RevenueIcon from "@mui/icons-material/PaymentsOutlined";
import ProductsIcon from "@mui/icons-material/Inventory2Outlined";
import StreamingIcon from "@mui/icons-material/LiveTvOutlined";
import SignageIcon from "@mui/icons-material/SlideshowOutlined";
// Distinct from StreamingIcon on purpose: collapsed, the rail is nothing but
// glyphs, and two areas sharing one glyph is two areas you cannot tell apart.
import GuestTvIcon from "@mui/icons-material/WebAssetOutlined";
import AppearanceIcon from "@mui/icons-material/PaletteOutlined";
import GlobalMediaIcon from "@mui/icons-material/PublicOutlined";
import DistributionIcon from "@mui/icons-material/HubOutlined";
import EffectChannelIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import VodIcon from "@mui/icons-material/VideoLibraryOutlined";
import IngestIcon from "@mui/icons-material/CloudUploadOutlined";
import CarouselIcon from "@mui/icons-material/ViewCarouselOutlined";
import PackageIcon from "@mui/icons-material/Inventory2Outlined";
import CareIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import OverviewIcon from "@mui/icons-material/DashboardOutlined";
import FleetHealthIcon from "@mui/icons-material/MonitorHeartOutlined";
import TvIcon from "@mui/icons-material/TvOutlined";
import OtaIcon from "@mui/icons-material/RocketLaunchOutlined";
import GroupsIcon from "@mui/icons-material/GroupWorkOutlined";
import AlertIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MeteredUsageIcon from "@mui/icons-material/SpeedOutlined";
import ClientErrorsIcon from "@mui/icons-material/BugReportOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BrandIcon from "@mui/icons-material/LiveTvOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth, userCan } from "../auth/AuthContext";
import { CUSTOMER_HOME, isCustomerSession } from "../auth/audience";
import {
  OVERVIEW_PATHS,
  effectiveOverviewProduct,
  isOverviewPath,
  overviewProductsFor,
} from "../nav/customerOverview";
import type { PermissionKey } from "@shared/types/auth";
import { useThemeMode } from "../ThemeMode";
import { useScope } from "../context/ScopeContext";
import { usePremiseWorkspace } from "../context/PremiseWorkspace";
import {
  CHAIN_CAPABLE_SECTIONS,
  productsForCompany,
  productsForPremise,
} from "../nav/products";
import { PageChromeCtx } from "../context/PageChrome";
import AppHeader from "./AppHeader";
import CommandPalette, { PALETTE_EVENT, PALETTE_HINT } from "./CommandPalette";
import {
  RailGroup,
  RailItem,
  RAIL_ITEM,
  RAIL_WIDTH,
  type RailLink,
} from "./NavRail";

const WIDTH_OPEN = 264;
const WIDTH_COLLAPSED = RAIL_WIDTH;

export interface NavItem {
  label: string;
  href: string;
  testId: string;
  icon: React.ReactNode;
  /** When set, the item only appears if user.type matches. */
  adminOnly?: boolean;
  /**
   * When set, the item only appears if the user has this permission — or ANY
   * of them, when it is a list. A list is what a grandfather clause looks like
   * in the nav: a brand-new key nobody has been granted yet, beside the broad
   * key the people who need the surface already hold. Keep it in step with the
   * route's own `requireAnyPermission`, or the section hides itself from users
   * the server would let in.
   */
  requirePermission?: string | string[];
  /** Overrides the default "pathname starts with href" highlight. Needed by
   *  the premise sections, which differ only by ?section=. */
  activeWhen?: (pathname: string, search: string) => boolean;
  /** Greys the item out and explains why, instead of hiding it. A nav that
   *  changes shape as you move is a nav you can't navigate back through. */
  disabledReason?: string;
  /** A third level, shown while this item is the active one. */
  children?: NavItem[];
}

const primaryItems: NavItem[] = [
  {
    label: "Companies",
    href: "/companies",
    testId: "root-nav-companies",
    icon: <BusinessIcon />,
    // Nenda staff only. A customer has exactly one company - theirs - so the
    // tenant list is not a page for them; their locations are reached through
    // "Places & Screens". The server refuses an admin with a company
    // (`admin_has_no_tenant_scope`), so `adminOnly` means Nenda staff.
    adminOnly: true,
  },
  {
    label: "Demo Studio",
    href: "/demo-studio",
    testId: "root-nav-demo-studio",
    icon: <DemoStudioIcon />,
    // Salespeople are platform admins (Sigge, 2026-09-25): the demos it builds
    // live under Nenda Demo, which only Nenda staff can see.
    adminOnly: true,
  },
];

/**
 * Items grouped under the collapsible "Overview" section - the fleet-wide
 * views. It sits at the very top of the nav because "Fleet health" is where
 * the CMS lands on sign-in.
 */
const fleetChildren: NavItem[] = [
  {
    /*
     * FIRST, deliberately.
     *
     * A platform admin gets it beside the other estate-wide views. A company
     * or premise admin is permitted none of those, so this group collapses to
     * one item and renders flat - which is why the label reads "Screens" and
     * not "Signage overview": for that reader it is not a section of anything,
     * it is the page.
     */
    label: "Screens",
    href: "/signage-overview",
    testId: "root-nav-signage-overview",
    icon: <TvIcon />,
    requirePermission: ["canManageSignage", "canEditLayout"],
  },
  {
    label: "Fleet health",
    href: "/analytics",
    testId: "root-nav-fleet",
    icon: <FleetHealthIcon />,
    requirePermission: "canViewFleet",
  },
  {
    label: "Device groups",
    href: "/device-groups",
    testId: "root-nav-device-groups",
    icon: <GroupsIcon />,
    requirePermission: "canViewFleet",
  },
  {
    label: "OTA Rollouts",
    href: "/ota",
    testId: "root-nav-ota",
    icon: <OtaIcon />,
    requirePermission: "canManageOta",
  },
  {
    label: "Health alerting",
    href: "/health-alerts",
    testId: "root-nav-health-alerts",
    icon: <AlertIcon />,
    requirePermission: "canViewFleet",
  },
  {
    // Same permission as the rest of this group deliberately: the ad-rotation
    // shortfall this dashboard exists to catch is a fleet-health question,
    // and "who can see it" was settled 2026-09-14 as "same audience as Fleet
    // health" (sales included) - the "Metered usage" TOGGLE itself is
    // separately admin-only, on the premise Overview tab.
    label: "Metered usage",
    href: "/metered-usage",
    testId: "root-nav-metered-usage",
    icon: <MeteredUsageIcon />,
    requirePermission: "canViewFleet",
  },
  {
    // Nenda staff only: the list spans every customer's CMS and guest pages.
    // canManageSettings is admin-only; customer ops users hold canViewFleet.
    label: "Client errors",
    href: "/client-errors",
    testId: "root-nav-client-errors",
    icon: <ClientErrorsIcon />,
    requirePermission: "canManageSettings",
  },
];

/**
 * A product area and the sections it contributes. An area appears only where
 * its product is licensed (see nav/products.ts) - which is what lets a hotel
 * that also bought signage, or a premise that bought nothing but signage, have
 * a menu that fits it.
 */
export interface AreaSection {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Hidden unless the scoped premise is care / is not care. */
  only?: "care" | "hotel";
  /**
   * Nenda staff only: hidden from a customer session (`isCustomerSession`),
   * and a customer who opens it by URL is sent to the Dashboard.
   */
  staffOnly?: boolean;
  /** One key, or any of several — see `NavItem.requirePermission`. */
  requirePermission?: string | string[];
  /** Hidden from a user holding ANY of these keys (a page for a narrower tier). */
  hiddenFor?: string[];
  /**
   * Hidden unless this template knob is ON.
   *
   * For a section that exists to feed ONE screen: flipping that screen off on
   * the Screens board should take its authoring page with it, or an operator is
   * left filling in a catalogue that reaches no television. One switch, both
   * halves.
   */
  requireKnob?: string;
}

/**
 * Ruled by Sigge 2026-09-07: compressed from seven items to five, and moved
 * OUT of this sidebar entirely into a top tab bar that `WorkspacePage`
 * renders once a premise is open (it imports this same array, so the tabs
 * and this list can never drift apart). The sidebar's own "Places &
 * Screens" entry is now a single link straight to "overview" (the
 * Dashboard) - see the `renderGroup` call below, which no longer receives
 * this as `children`. `codes` (Installation code) and `pms` (PMS) are gone
 * as destinations of their own; both are now sub-tabs of `settings`.
 */
export const PLACE_SECTIONS: AreaSection[] = [
  // The two you open every day sit at the top; the setup pages follow.
  // `key` is the route segment - "overview" stays so old links keep working.
  { key: "overview", label: "Dashboard", icon: <PremiseOverviewIcon /> },
  { key: "devices", label: "Screens", icon: <DevicesIcon /> },
  // Nenda-side setup (Sigge, 2026-09-28): the place's general config, its
  // installation codes and its PMS link. A customer does not see it.
  { key: "settings", label: "Settings", icon: <TuneIcon />, staffOnly: true },
  // Nenda-side too for a customer (Sigge, 2026-09-29): what was sold, and the
  // room list the PMS / Nenda maintains.
  {
    key: "products",
    label: "Products",
    icon: <ProductsIcon />,
    staffOnly: true,
  },
  { key: "rooms", label: "Rooms", icon: <RoomTypeIcon />, staffOnly: true },
];

/**
 * Start & navigation ("Start och navigering", Sigge 2026-09-29) - the
 * Frontlayer product's menu. It replaced a group whose first entry carried the
 * template editor's twenty tabs as a nested sub-menu; those now live inside
 * Home screen (what the guest's menu shows) and Appearance (how it looks), each
 * a list / editor / preview page with breadcrumbs.
 *
 * Media is the shared library: Signage lists the same panel as "Media
 * library", and `CHAIN_CAPABLE_SECTIONS` keeps it reachable at customer scope
 * while the rest of the area - which needs a location - stays hidden.
 *
 * Guest web is its own menu entry (see the render below), and the home-screen
 * promotion slot is edited only under Promotions.
 */
export const FRONTLAYER_SECTIONS: AreaSection[] = [
  { key: "home", label: "Home screen", icon: <TemplatesIcon /> },
  { key: "media", label: "Media", icon: <PermMediaIcon /> },
  // Appearance and Languages are Nenda's to set up, not the customer's
  // (Sigge, 2026-09-29): hidden from company and premise admins.
  {
    key: "appearance",
    label: "Appearance",
    icon: <AppearanceIcon />,
    staffOnly: true,
  },
  // Translates the TEMPLATE's content and nothing else, so it sits with it.
  {
    key: "translate",
    label: "Languages",
    icon: <TranslateIcon />,
    staffOnly: true,
  },
  { key: "schedule", label: "Scheduling", icon: <ScheduleIcon /> },
];

/**
 * Everything about what the television SELLS, in one place.
 *
 * Offers began as a Frontlayer section (`docs/room-offers.md` §9) on the
 * reasoning that it renders into a template's screens and so is not a product
 * of its own. That held while it was one catalogue; it stopped holding once the
 * same feature grew a home-screen placement, a staff inbox and a revenue
 * report. Those four answer one question - "what are we selling, and did anyone
 * buy it?" - and they are bought, worked and read by different people from the
 * ones who choose a wallpaper.
 *
 * NOTE the cost §9 warned about, which is real: this is now gated by a
 * `License.product` row, and `products.ts` implies nothing. A place that has
 * not been sold Promotions has no Promotions menu, however many offers sit in
 * its catalogue. That is the point of a product, but it means the licence has
 * to be recorded on the place's Products page first.
 */
const PROMOTION_SECTIONS: AreaSection[] = [
  { key: "offers", label: "Offers", icon: <OffersIcon />, only: "hotel" },
  { key: "slots", label: "Promotions", icon: <TemplatesIcon />, only: "hotel" },
  {
    key: "requests",
    label: "Requests",
    icon: <RequestsIcon />,
    only: "hotel",
    // `canEditLayout` is the grandfather clause the route carries too
    // (`guestRequests.ts`): the new key is stored per user and nothing
    // backfills it, so gating on it alone hides the section from every
    // existing operator until somebody re-saves their role.
    requirePermission: ["canHandleGuestRequests", "canEditLayout"],
  },
  {
    key: "revenue",
    label: "Revenue",
    icon: <RevenueIcon />,
    only: "hotel",
    requirePermission: [
      "canManageOffers",
      "canHandleGuestRequests",
      "canEditLayout",
    ],
  },
];

const CARE_SECTIONS: AreaSection[] = [
  { key: "residents", label: "Residents", icon: <PeopleIcon /> },
];

/**
 * Streaming TV has no editor yet - the CMS does not provision channels. It
 * still gets a section: a product you switched on and cannot see anywhere is
 * indistinguishable from a switch that did not work.
 */

/**
 * Signage's four sections, in the order the design defines them
 * (Planera innehåll / Kampanjer / Kanaler / Mediabibliotek).
 *
 * `library` is the shared customer/location media library - the same panel the
 * top-level "Content" entry opens, and the same one Frontlayer calls "media".
 * It is listed here because the product's own IA puts it here, but the
 * top-level entry stays: a library nested under a product group is unreachable
 * when a customer is scoped WITHOUT a location, because product groups are
 * derived from the scoped location. Both routes land on the same panel and the
 * header scope decides which layer it opens, so the duplication costs nothing
 * and buys reachability.
 */
/**
 * Who may author signage. Every section below carries a gate since the
 * care-unit tier (`canEditSignageContent`, 2026-09-23) arrived: before it,
 * everyone who could see Signage could use all of it, so no section needed
 * one. A ward's staff now see "Update screens" and nothing that would 403.
 */
const SIGNAGE_AUTHORS = ["canManageSignage", "canEditLayout"];

const SIGNAGE_SECTIONS: AreaSection[] = [
  {
    key: "plan",
    label: "Plan content",
    icon: <ScheduleIcon />,
    requirePermission: SIGNAGE_AUTHORS,
  },
  {
    key: "campaigns",
    label: "Campaigns",
    icon: <CampaignIcon />,
    requirePermission: SIGNAGE_AUTHORS,
  },
  {
    key: "channels",
    label: "Channels",
    icon: <TemplatesIcon />,
    requirePermission: SIGNAGE_AUTHORS,
  },
  // Sets of screens, addressed as one thing. Beside the content sections
  // rather than under Locations & Screens because a group exists to be a
  // TARGET for content - what plays where is the question it answers.
  {
    key: "groups",
    label: "Groups",
    icon: <ScreenGroupsIcon />,
    requirePermission: SIGNAGE_AUTHORS,
  },
  // Designs and the slides filled in from them. Its own section rather than a
  // fourth tab on Plan content: that page is where you arrange WHAT PLAYS WHEN,
  // and a template is a thing you make long before you schedule it.
  {
    key: "templates",
    label: "Templates",
    icon: <DashboardCustomizeIcon />,
    requirePermission: [...SIGNAGE_AUTHORS, "canAuthorSignageTemplates"],
  },
  // The care unit's page: change the unlocked parts of existing slides, on
  // the slide itself. It exists for the tier that sees nothing else, and
  // ONLY that tier sees it: an author has the full editors, and Sigge took it
  // out of their menu (2026-09-28). Still reachable by URL - it is their data.
  {
    key: "updates",
    label: "Update screens",
    icon: <EditNoteIcon />,
    requirePermission: [...SIGNAGE_AUTHORS, "canEditSignageContent"],
    hiddenFor: SIGNAGE_AUTHORS,
  },
  { key: "library", label: "Media library", icon: <PermMediaIcon /> },
];

const ANALYTICS_SECTIONS: AreaSection[] = [
  { key: "engagement", label: "Engagement", icon: <InsightsIcon /> },
  { key: "energy", label: "Energy", icon: <EnergyIcon /> },
];

const secondaryItems: NavItem[] = [
  {
    label: "Users",
    href: "/users",
    testId: "root-nav-users",
    icon: <PeopleIcon />,
    requirePermission: "canManageUsers",
  },
];

/**
 * Distribution - how content reaches the screens, at PLATFORM level.
 *
 * These are Nenda's own catalogues, not a customer's: the effect channels the
 * legacy autoplayers pull, the live TV line-up, the VOD shelf and the ingest
 * pipeline that fills them. Nothing here is scoped to a location, which is why
 * it sits in the Admin block rather than under a product.
 *
 * Gated by `canManageDistribution`, not by `adminOnly`. The permission is what
 * the server enforces on every Distribution route - reads included - so the nav
 * has to read the same flag, or a user who holds the key is refused the menu
 * that leads to routes that would let them in. `canManageDistribution` is itself
 * an `adminOnly` key in the catalogue and sits in no default role, so this
 * grants nobody anything new; it only makes the gate revocable per user and
 * visible in the permission UI, exactly like `canManageMedia` above.
 *
 * ONE right for the whole area, not one per section - see the key's own comment
 * in `src/shared/types/auth.ts` for why, and for the shape to copy if
 * "VOD but not the channel line-up" ever becomes a real job.
 */
export const distributionChildren: NavItem[] = [
  /**
   * ONE channels entry, not one per kind.
   *
   * Effect channels and linear channels were two menu items until they were
   * merged: they are the same object with a different source (a loop we
   * assemble vs a broadcast we carry), an operator manages them in one list,
   * and splitting them meant knowing which kind a channel was before you could
   * find it. The kind is now a filter on the page, which is what the design
   * does too.
   */
  {
    label: "Channels",
    href: "/distribution/channels",
    testId: "root-nav-distribution-channels",
    icon: <EffectChannelIcon />,
    requirePermission: "canManageDistribution",
  },
  /**
   * Directly under Channels, because it is the same subject and the pairing is
   * the fastest way to learn the distinction: Channels is WHAT the platform
   * carries, Line-up is WHAT ORDER they appear in. Neither grants anything.
   */
  {
    label: "Line-up",
    href: "/distribution/lineup",
    testId: "root-nav-distribution-lineup",
    icon: <EffectChannelIcon />,
    requirePermission: "canManageDistribution",
  },
  {
    label: "VOD",
    href: "/distribution/vod",
    testId: "root-nav-distribution-vod",
    icon: <VodIcon />,
    requirePermission: "canManageDistribution",
  },
  /**
   * The carousel CATALOGUE (BE-28), gated exactly like the rest of the area.
   *
   * The SELECTION — which rails a room type shows, in what order — is NOT here:
   * it lives under Settings → Room types, on `canManageRoomTypes`, because it
   * has always been the customer's to author and never Nenda's.
   */
  {
    label: "Carousels",
    href: "/distribution/carousels",
    testId: "root-nav-distribution-carousels",
    icon: <CarouselIcon />,
    requirePermission: "canManageDistribution",
  },
  /**
   * Content packages (BE-30) — what a room is ENTITLED to.
   *
   * Beside Carousels on purpose, and the pair is the distinction worth
   * seeing in a menu: `Package` grants ACCESS, `Carousel` grants
   * DISPLAY. A guest may be entitled to a film that appears in no rail, and a
   * rail may advertise a film the room cannot play.
   *
   * Trusting a supplier is a second key (`canTrustSuppliers`) enforced on the
   * route, not here: seeing the screen is part of managing the catalogue,
   * committing Nenda to a supplier is not.
   */
  /**
   * Products before Packages, deliberately.
   *
   * A product is what a customer BUYS; a package is the bag of content it
   * grants. Distribution used to lead with the bag, so the first thing an
   * operator met was implementation detail and nothing on screen said what
   * anybody had been sold. The nav order is the mental model.
   */
  {
    label: "Products",
    href: "/distribution/products",
    testId: "root-nav-distribution-products",
    icon: <PackageIcon />,
    requirePermission: "canManageDistribution",
  },
  {
    label: "Packages",
    href: "/distribution/packages",
    testId: "root-nav-distribution-packages",
    icon: <PackageIcon />,
    requirePermission: "canManageDistribution",
  },
  /**
   * Content providers.
   *
   * Its own entry rather than a card on Packages, because a provider is not a
   * detail of a package: `entitlementResolver` (the play gate), `bulkIngestScan`
   * and the content-owner portal all read `ContentSupplier`, and packages are
   * the last of the four to matter.
   *
   * Grouped with Review queue and Bulk ingest deliberately — those three are the
   * SUPPLY side (who gives us content, what they sent, is it fit to show),
   * where Channels/VOD/Carousels/Products/Packages are the SELL side. The nav
   * order is the mental model, the same reason Products precedes Packages.
   */
  {
    label: "Content providers",
    href: "/distribution/providers",
    testId: "root-nav-distribution-providers",
    icon: <SupplierIcon />,
    requirePermission: "canManageDistribution",
  },
  /**
   * The review queue (`CONTENT-17`).
   *
   * `canManageDistribution` and no second key: a reviewer is by definition
   * somebody allowed to see every supplier's material, and that key already
   * gates the whole catalogue including reads.
   */
  {
    label: "Review queue",
    href: "/distribution/submissions",
    testId: "root-nav-distribution-submissions",
    icon: <ReviewIcon />,
    requirePermission: "canManageDistribution",
  },
  {
    label: "Bulk ingest",
    href: "/distribution/bulk-ingest",
    testId: "root-nav-distribution-bulk-ingest",
    icon: <IngestIcon />,
    requirePermission: "canManageDistribution",
  },
];

/** Items grouped under the collapsible "Settings" section. */
const settingsChildren: NavItem[] = [
  {
    label: "General settings",
    href: "/settings/general",
    testId: "root-nav-general-settings",
    icon: <TuneIcon />,
    requirePermission: "canManageSettings",
  },
  {
    label: "Room types",
    href: "/room-types",
    testId: "root-nav-room-types",
    icon: <RoomTypeIcon />,
    adminOnly: true,
  },
  {
    label: "Templates",
    href: "/templates",
    testId: "root-nav-templates",
    icon: <TemplatesIcon />,
    requirePermission: "canManageSchemeDefaults",
  },
  {
    label: "Radio",
    href: "/radio-stations",
    testId: "root-nav-radio",
    icon: <RadioIcon />,
    requirePermission: "canManageRadioStations",
  },
  {
    label: "Apps",
    href: "/apps",
    testId: "root-nav-apps",
    icon: <AppsIcon />,
    requirePermission: "canManageApps",
  },
  /**
   * The PLATFORM library - app icons, radio logos, Nenda's own artwork - not a
   * customer's own files. It used to be a top-level entry of its own; it now
   * sits with the other global catalogues it exists to serve (Templates, Radio,
   * Apps all draw their logos from it), which is where an admin already is when
   * they need it. `canManageMedia` is adminOnly, so nobody outside Nenda staff
   * sees the entry at all.
   *
   * A customer's own library is reached inside the product that uses it -
   * Frontlayer > Content, Signage > Media library - both of which open the same
   * panel, with the header scope deciding which layer.
   */
  {
    label: "Global media",
    href: "/media",
    testId: "root-nav-media",
    icon: <GlobalMediaIcon />,
    requirePermission: "canManageMedia",
  },
];

/**
 * Detail pages (where the user wants maximum canvas area) auto-collapse the
 * drawer. The user can override either way via the toggle button.
 */
function pathPrefersCollapsed(pathname: string): boolean {
  // NB: premise detail used to collapse too. It no longer does - the premise's
  // own sections live in this nav now, and a mini rail hides them.
  return /^\/templates\/[^/]+/.test(pathname);
}

export default function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggle: toggleMode } = useThemeMode();
  const { scope, premises } = useScope();
  const { draft } = usePremiseWorkspace();
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const userInitial = user?.user?.trim().charAt(0).toUpperCase() ?? "?";

  /**
   * Below `md` the nav stops being furniture and becomes a thing you open.
   *
   * A 264px permanent drawer leaves about 126px of a 390px phone for the page
   * itself, which is what made the CMS unusable on a handset rather than merely
   * cramped: every screen was being asked to lay itself out in a third of the
   * viewport. On a phone it becomes a `temporary` drawer over the content, at
   * its FULL width - the collapsed icon rail is a desktop affordance for
   * reclaiming space, and there is no space to reclaim when the drawer is only
   * on screen because you asked for it.
   */
  const isHandset = useMediaQuery("(max-width:899.95px)");
  const [navOpen, setNavOpen] = useState(false);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem("foyer.root-collapsed");
    if (stored != null) return stored === "1";
    return pathPrefersCollapsed(location.pathname);
  });
  const prevPath = useRef(location.pathname);

  // Tapping a nav item on a phone must also dismiss the sheet it was tapped in.
  // Keyed on the path rather than wired into every button, so a navigation from
  // anywhere inside the drawer - including the nested accordions - closes it.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Auto-collapse when entering a detail page; auto-expand when going back to
  // a list. The user can still override via the toggle.
  useEffect(() => {
    const wasDetail = pathPrefersCollapsed(prevPath.current);
    const isDetail = pathPrefersCollapsed(location.pathname);
    if (!wasDetail && isDetail) setCollapsed(true);
    if (wasDetail && !isDetail) setCollapsed(false);
    prevPath.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("foyer.root-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const [settingsOpen, setSettingsOpen] = useState<boolean>(() =>
    settingsChildren.some((c) => location.pathname.startsWith(c.href))
  );
  const [fleetOpen, setFleetOpen] = useState<boolean>(() =>
    fleetChildren.some((c) => location.pathname.startsWith(c.href))
  );
  const [distributionOpen, setDistributionOpen] = useState<boolean>(() =>
    location.pathname.startsWith("/distribution")
  );
  // Which product areas are expanded. Opening one never closes another - these
  // are peers, not tabs.
  const [openAreas, setOpenAreas] = useState<Set<string>>(
    () => new Set(["places"])
  );
  const toggleArea = useCallback(
    (key: string) =>
      setOpenAreas((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );

  // Navigating INTO a group opens it - otherwise landing straight on Fleet
  // health (the home redirect does exactly that) leaves the active item
  // hidden inside a closed accordion. Closing one by hand still sticks.
  useEffect(() => {
    if (fleetChildren.some((c) => location.pathname.startsWith(c.href))) {
      setFleetOpen(true);
    }
    if (settingsChildren.some((c) => location.pathname.startsWith(c.href))) {
      setSettingsOpen(true);
    }
    if (location.pathname.startsWith("/distribution")) {
      setDistributionOpen(true);
    }
    // Landing anywhere inside an area expands it.
    for (const base of [
      "places",
      "streaming-tv",
      "frontlayer",
      "signage",
      "care",
    ]) {
      // A path segment, not a string prefix: "/signage-overview" (a
      // customer's landing page) is not inside "/signage", and a bare prefix
      // match opened the Signage group on every sign-in.
      if (
        location.pathname === `/${base}` ||
        location.pathname.startsWith(`/${base}/`)
      ) {
        setOpenAreas((prev) => new Set(prev).add(base));
      }
    }
    if (location.pathname.startsWith("/premises")) {
      setOpenAreas((prev) => new Set(prev).add("places"));
    }
    if (location.pathname.startsWith("/content-analytics")) {
      setOpenAreas((prev) => new Set(prev).add("analytics"));
    }
  }, [location.pathname]);

  // The premise every product area works on: whatever the header's Location
  // points at. There is no second source - opening a premise sets the header
  // (see PremiseRedirect), so the nav and the pages can never disagree.
  const scoped = premises.find((p) => p.premiseId === scope.premiseId);
  // No location, no products. A premise's products are a fact about that
  // premise - showing a union across the estate, or a greyed-out branch,
  // implies a place that does not exist yet.
  const products = useMemo(
    () => (scope.premiseId ? productsForPremise(scoped) : new Set<string>()),
    [scope.premiseId, scoped]
  );
  const hasPremise = !!scope.premiseId;

  /**
   * Products the scoped CUSTOMER does somewhere, used only for the areas that
   * have a real customer-level surface.
   *
   * The rule above still holds for everything else: a union cannot unlock a
   * per-location editor. But chain-level signage authoring is a fact about the
   * customer, not about any one place - a media manager hired by the chain
   * builds playlists for all of it - so the Signage group has to survive
   * "customer picked, no location". Without this, that job is unreachable.
   */
  const customer = isCustomerSession(user);
  const customerOverviewProduct = customer
    ? effectiveOverviewProduct(scope.product, overviewProductsFor(premises))
    : null;
  const companyProducts = useMemo(() => {
    if (scope.premiseId) return null;
    if (scope.companyId) return productsForCompany(scope.companyId, premises);
    // A customer never picks a customer - they ARE one. Their premise list is
    // already filtered to what they may reach (server-side), so its products
    // are what they bought, and the product areas are where they work
    // (Sigge, 2026-09-28). Each area's pages ask for a location where they
    // need one. Nenda staff with no customer picked still see none.
    if (customer) {
      const all = new Set<string>();
      for (const p of premises) {
        for (const key of productsForPremise(p)) all.add(key);
      }
      return all;
    }
    return null;
  }, [scope.premiseId, scope.companyId, premises, customer]);
  /** Areas reachable per-location OR chain-wide (they own a chain-capable section). */
  const showSignage = hasPremise
    ? products.has("signage")
    : Boolean(companyProducts?.has("signage"));
  const showFrontlayer = hasPremise
    ? products.has("frontlayer")
    : Boolean(companyProducts?.has("frontlayer"));
  // Chain-capable: offers are scoped premise/company/global, so a marketing
  // manager authors them with no location picked (POS-14).
  const showPromotions = hasPremise
    ? products.has("promotions")
    : Boolean(companyProducts?.has("promotions"));
  // Per-location areas: only ever offered without a location to a CUSTOMER,
  // whose union is their own estate. Their pages show "Pick a location".
  const showStreamingTv = hasPremise
    ? products.has("streaming-tv")
    : customer && Boolean(companyProducts?.has("streaming-tv"));
  const showVideoCalling = hasPremise
    ? products.has("video-call")
    : customer && Boolean(companyProducts?.has("video-call"));
  const showCare = hasPremise
    ? products.has("care")
    : customer && Boolean(companyProducts?.has("care"));
  const showAnyProduct =
    showStreamingTv ||
    showFrontlayer ||
    showPromotions ||
    showSignage ||
    showVideoCalling ||
    showCare;

  /** Turn an area's sections into nav items under its route base. */
  const areaItems = useCallback(
    (base: string, sections: AreaSection[]): NavItem[] =>
      sections
        // With no location picked, only the sections that mean something for a
        // whole customer survive. The rest need a place and would open on an
        // empty page.
        //
        // Except for a customer (Sigge, 2026-09-28): the product areas are
        // where they work, so every section of what they bought is listed, and
        // a per-location one opens WorkspacePage's "Pick a location" page
        // rather than an empty one.
        .filter(
          (sec) => hasPremise || customer || CHAIN_CAPABLE_SECTIONS.has(sec.key)
        )
        // Nenda-side sections never reach a customer's menu.
        .filter((sec) => !(customer && sec.staffOnly))
        .filter((sec) => {
          if (!sec.only) return true;
          const fits = (p: { segment?: string }) =>
            sec.only === "care" ? p.segment === "care" : p.segment !== "care";
          if (scoped) return fits(scoped);
          // No location: a customer sees a section when one of THEIR places
          // is of that kind - a hotel chain is not offered care-only pages.
          return customer ? premises.some(fits) : true;
        })
        // A knob-gated section follows its screen. With no premise scoped there
        // is no template to ask, so the gate cannot answer and the section stays
        // - hiding it at customer scope would take chain authoring away too.
        .filter(
          (sec) =>
            !sec.hiddenFor?.some((key) => userCan(user, key as PermissionKey))
        )
        .filter((sec) => {
          if (!sec.requireKnob || !hasPremise) return true;
          return draft?.scheme?.knobs?.[sec.requireKnob] === true;
        })
        .map((sec) => ({
          label: sec.label,
          testId: `root-nav-${base.slice(1)}-${sec.key}`,
          icon: sec.icon,
          href: `${base}/${sec.key}`,
          requirePermission: sec.requirePermission,
        })),
    [scoped, hasPremise, draft?.scheme?.knobs, customer, premises, user]
  );

  // Full width on a handset: see `isHandset`. The collapse toggle still works
  // on desktop and its stored preference is untouched, so rotating a tablet
  // back over the breakpoint restores whatever the user last chose.
  const width = isHandset
    ? WIDTH_OPEN
    : collapsed
      ? WIDTH_COLLAPSED
      : WIDTH_OPEN;

  // The header's title slot. PageHeader portals into it and tells us it did,
  // so the bar only falls back to a route-derived title when nothing else has.
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
  const [headerClaims, setHeaderClaims] = useState(0);
  const claim = useCallback(
    (delta: 1 | -1) => setHeaderClaims((n) => Math.max(0, n + delta)),
    []
  );
  // Nothing in this file wires a `slotRef` into it any more - "Places &
  // Screens" is a flat link since 2026-09-07 (see `PLACE_SECTIONS`'s own
  // comment) - but `chrome.navSlot`/`claimNav` stay part of the context's
  // shape, since `PremiseEditorPage` still reads them.
  const [navSlot, _setNavSlot] = useState<HTMLDivElement | null>(null);
  const [_navClaims, setNavClaims] = useState(0);
  const claimNav = useCallback(
    (delta: 1 | -1) => setNavClaims((n) => Math.max(0, n + delta)),
    []
  );
  const chrome = useMemo(
    () => ({ slot: headerSlot, claim, navSlot, claimNav }),
    [headerSlot, claim, navSlot, claimNav]
  );

  /** Whether the signed-in user may see a nav item. */
  function canSee(it: NavItem): boolean {
    if (it.adminOnly && user?.type !== "admin") return false;
    if (it.requirePermission) {
      const wanted = Array.isArray(it.requirePermission)
        ? it.requirePermission
        : [it.requirePermission];
      // `userCan`, not a raw `includes`: it applies the staff-only-key rule
      // the server enforces, so the menu and the API agree.
      return wanted.some((key) => userCan(user, key as PermissionKey));
    }
    return true;
  }

  /** Render a single nav button (with a tooltip when the drawer is collapsed). */
  function renderNavItem(it: NavItem, indent = false) {
    const active = it.activeWhen
      ? it.activeWhen(location.pathname, location.search)
      : location.pathname.startsWith(it.href);
    const button = (
      <ListItemButton
        key={it.testId}
        selected={active}
        disabled={!!it.disabledReason}
        onClick={() => navigate(it.href)}
        data-testid={it.testId}
        sx={{
          justifyContent: collapsed ? "center" : "flex-start",
          mx: collapsed ? 0.5 : 1,
          my: 0.25,
          borderRadius: 2,
          px: collapsed ? 0 : 1.5,
          pl: !collapsed && indent ? 3.5 : undefined,
        }}
      >
        <ListItemIcon
          sx={{ minWidth: collapsed ? 0 : 40, justifyContent: "center" }}
        >
          {it.icon}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={it.label} />}
      </ListItemButton>
    );
    // A third level (the template's tabs) unfolds only under the active item.
    if (!collapsed && it.children?.length && active) {
      return (
        <Box key={it.testId}>
          {button}
          <List disablePadding sx={{ pl: 1.5 }}>
            {it.children.map((c) => renderNavItem(c, true))}
          </List>
        </Box>
      );
    }
    if (collapsed) {
      return (
        <RailItem
          key={it.testId}
          icon={it.icon}
          label={it.label}
          active={active}
          testId={it.testId}
          onClick={() => navigate(it.href)}
        />
      );
    }
    if (it.disabledReason) {
      return (
        <Tooltip key={it.testId} title={it.disabledReason} placement="right">
          {/* A disabled button fires no events - the span carries the hover. */}
          <span style={{ display: "block" }}>{button}</span>
        </Tooltip>
      );
    }
    return button;
  }

  /** Render a collapsible nav group (parent toggle + indented children). */
  function renderGroup(opts: {
    label: string;
    icon: React.ReactNode;
    testId: string;
    children: NavItem[];
    open: boolean;
    toggle: () => void;
    sectionLabel?: string;
    /** When set, the parent row is also a link (and always expands). */
    href?: string;
    /** Host slot for a page-supplied sub-nav (the premise editor's sections). */
    slotRef?: (el: HTMLDivElement | null) => void;
    /** True when a page has filled that slot - hides the static children. */
    slotClaimed?: boolean;
  }) {
    const visible = opts.children.filter(canSee);
    // A childless group is still worth showing when it is itself a link -
    // that is how "No products yet" points at the page that fixes it.
    if (visible.length === 0 && !opts.href) return null;
    /*
     * A GROUP OF ONE IS NOT A GROUP.
     *
     * The same nav serves a platform admin, who sees four fleet-wide views
     * under Overview, and a premise admin, who is permitted exactly one of
     * them. For the second reader an accordion is a disclosure control over a
     * single item: one extra click, and a chevron that promises something
     * behind it. Rendered flat, they simply get the page.
     *
     * Only when the group is not itself a link - a group with an `href` is a
     * destination in its own right and keeps its shape.
     */
    if (visible.length === 1 && !opts.href && !opts.slotRef) {
      return renderNavItem({
        ...visible[0]!,
        icon: visible[0]!.icon ?? opts.icon,
      });
    }
    // Mini drawer: no accordion - show the group's items as flat icons.
    if (collapsed) {
      const links: RailLink[] = visible.map((it) => ({
        label: it.label,
        href: it.href,
        testId: `rail-${it.testId}`,
        disabled: !!it.disabledReason,
        active: it.activeWhen
          ? it.activeWhen(location.pathname, location.search)
          : location.pathname.startsWith(it.href),
      }));
      return (
        <RailGroup
          key={opts.testId}
          icon={opts.icon}
          label={opts.label}
          links={links}
          testId={opts.testId}
          active={links.some((l) => l.active)}
          onNavigate={navigate}
          onSelfClick={opts.href ? () => navigate(opts.href!) : undefined}
        />
      );
    }
    return (
      <Box key={opts.testId}>
        {opts.sectionLabel && (
          <Typography
            variant="overline"
            sx={{ display: "block", px: 2.5, pt: 2, pb: 0.5 }}
          >
            {opts.sectionLabel}
          </Typography>
        )}
        <ListItemButton
          selected={!!opts.href && location.pathname === opts.href}
          onClick={() => {
            // The row toggles in BOTH directions - a control that only opens
            // is a control you have to learn twice. A linked group also
            // navigates: the parent is the list page, the children are the
            // sections of one entry in it.
            if (opts.href) navigate(opts.href);
            opts.toggle();
          }}
          data-testid={opts.testId}
          sx={{ mx: 1, my: 0.25, borderRadius: 2, px: 1.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40, justifyContent: "center" }}>
            {opts.icon}
          </ListItemIcon>
          <ListItemText primary={opts.label} />
          {visible.length > 0 && (
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => {
                // The chevron only folds - it never navigates.
                e.stopPropagation();
                opts.toggle();
              }}
              sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
            >
              {opts.open ? <ExpandLess /> : <ExpandMore />}
            </Box>
          )}
        </ListItemButton>
        <Collapse in={opts.open} timeout="auto" unmountOnExit>
          {opts.slotRef && <Box ref={opts.slotRef} />}
          {!opts.slotClaimed && (
            <List disablePadding>
              {visible.map((it) => renderNavItem(it, true))}
            </List>
          )}
        </Collapse>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Drawer
          variant={isHandset ? "temporary" : "permanent"}
          open={isHandset ? navOpen : undefined}
          onClose={() => setNavOpen(false)}
          // The nav is the whole app's index; re-mounting it on every open
          // would re-run the accordion state and lose the user's expansion.
          ModalProps={{ keepMounted: true }}
          sx={{
            width,
            // A temporary drawer sits OVER the content, so it must not also
            // reserve a 264px column - that would restore the exact squeeze
            // this change exists to remove.
            flexShrink: 0,
            ...(isHandset && { width: 0 }),
            transition: "width 200ms ease",
            [`& .MuiDrawer-paper`]: {
              width,
              boxSizing: "border-box",
              // Visible so the collapsed rail's expand button can sit on the
              // edge, as in the design. BOTH axes have to be visible - a
              // single `auto` axis makes the other one clip too. The nav list
              // does the scrolling instead.
              overflowX: "visible",
              overflowY: "visible",
              transition: "width 200ms ease",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "space-between",
              px: collapsed ? 0 : 2,
              gap: 1,
              position: "relative",
              overflow: "visible",
            }}
          >
            {collapsed ? (
              // Collapsed: the mark in a tinted tile. It has to carry its own
              // colour - a paper-on-paper tile is invisible on this drawer, and
              // all you saw was a stray drop shadow.
              <Box
                onClick={() => navigate("/")}
                data-testid="root-home"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  cursor: "pointer",
                  transition: "background-color 150ms ease",
                  "&:hover": { bgcolor: "primary.dark" },
                  "& svg": { fontSize: 24 },
                }}
              >
                <BrandIcon />
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Montserrat, Inter, sans-serif",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  cursor: "pointer",
                  userSelect: "none",
                  color: "primary.main",
                }}
                onClick={() => navigate("/")}
                data-testid="root-home"
              >
                Foyer
              </Typography>
            )}
            {/* Collapsed: the toggle hangs off the rail's edge. Expanded: it
                sits inline where the label ends. */}
            <IconButton
              onClick={() => setCollapsed((c) => !c)}
              data-testid="root-toggle"
              aria-label={collapsed ? "expand menu" : "collapse menu"}
              size="small"
              sx={
                collapsed
                  ? {
                      position: "absolute",
                      right: -14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 28,
                      height: 28,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                      "&:hover": { bgcolor: "background.paper" },
                    }
                  : undefined
              }
            >
              {collapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </Toolbar>
          {/* The premise sections make this list long - it scrolls on its own so
            the theme toggle and account row stay pinned to the bottom. */}
          <List
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              ...(collapsed && {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.25,
                pt: 1,
              }),
            }}
          >
            {/* Command palette trigger. Collapsed, it MUST be a RailItem and
                not a ListItemButton: MUI gives ListItemButton `flex-grow: 1`,
                and the collapsed list is a flex column - so the button ate
                every spare pixel and pushed the whole rail apart. */}
            {collapsed ? (
              <RailItem
                icon={<SearchIcon />}
                label={`Search (${PALETTE_HINT})`}
                testId="open-command-palette"
                onClick={() => window.dispatchEvent(new Event(PALETTE_EVENT))}
              />
            ) : (
              <Box
                onClick={() => window.dispatchEvent(new Event(PALETTE_EVENT))}
                data-testid="open-command-palette"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mx: 1,
                  my: 0.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  cursor: "pointer",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <SearchIcon fontSize="small" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Search…
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.75,
                    py: 0.1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    fontFamily: "ui-monospace, Menlo, monospace",
                  }}
                >
                  {PALETTE_HINT}
                </Typography>
              </Box>
            )}

            {customer
              ? // ONE entry for a customer (Sigge, 2026-09-29): the header's
                // product selector decides whether it is the Frontlayer
                // location dashboard or the Signage screens overview. The
                // fleet-health, device-group, OTA, alerting and metered views
                // are Nenda's estate tooling, and "Places & Screens" is what
                // the Frontlayer overview already is.
                renderNavItem({
                  label: "Overview",
                  href: customerOverviewProduct
                    ? OVERVIEW_PATHS[customerOverviewProduct]
                    : CUSTOMER_HOME,
                  testId: "root-nav-overview",
                  icon: <OverviewIcon />,
                  activeWhen: (pathname) =>
                    isOverviewPath(pathname) ||
                    pathname.startsWith("/places") ||
                    pathname.startsWith("/premises"),
                })
              : renderGroup({
                  label: "Overview",
                  icon: <OverviewIcon />,
                  testId: "root-nav-fleet-group",
                  children: fleetChildren,
                  open: fleetOpen,
                  toggle: () => setFleetOpen((o) => !o),
                })}

            {primaryItems.filter(canSee).map((it) => renderNavItem(it))}

            {/* ONE item, not a group - ruled by Sigge 2026-09-07. The five
                sections this used to expand into (Dashboard, Screens,
                Settings, Products, Rooms) now live in a top tab bar
                `WorkspacePage` renders once a premise is open, reading the
                same `PLACE_SECTIONS` this file exports.

                It goes to the premise LIST whenever there is a choice to
                make. It used to go straight to the scoped premise's Dashboard,
                and since the scope persists, a customer with several
                locations who had opened one never saw the list again (the
                Companies page, the admin's way in, is not theirs). Only a
                session with a single location skips the list - for them it
                is one row they would have to click through every time. */}
            {!customer && (
              <Box key="root-nav-places">
                {renderNavItem({
                  label: "Places & Screens",
                  href:
                    hasPremise && premises.length <= 1
                      ? "/places/overview"
                      : "/premises",
                  testId: "root-nav-places",
                  icon: <ApartmentIcon />,
                  activeWhen: (pathname) =>
                    pathname.startsWith("/places") ||
                    pathname.startsWith("/premises"),
                })}
              </Box>
            )}

            {!collapsed &&
              ((hasPremise && products.size > 0) || showAnyProduct) && (
                <Typography
                  variant="overline"
                  sx={{ display: "block", px: 2.5, pt: 2, pb: 0.5 }}
                >
                  Products
                </Typography>
              )}

            {/* Product areas - present only where the product was sold. With
                nothing sold the section still shows, pointing at the page that
                fixes it: a silently missing section reads as a broken menu. */}
            {hasPremise &&
              !customer &&
              products.size === 0 &&
              renderGroup({
                label: "No products yet",
                icon: <ProductsIcon />,
                testId: "root-nav-no-products",
                href: "/places/products",
                children: [],
                open: false,
                toggle: () => {},
                sectionLabel: "Products",
              })}

            {/* ONE item, not an accordion. Ruled by Sigge: channels, carousels
                and pages were three siblings under a collapsible group, and the
                split did not match the job — the menu a guest meets is one
                thing. `renderGroup` with no children and an `href` is how a
                group becomes a plain link. */}
            {showStreamingTv &&
              renderGroup({
                label: "Streaming TV",
                icon: <StreamingIcon />,
                testId: "root-nav-streaming-tv",
                children: [],
                href: "/streaming-tv/menu",
                open: false,
                toggle: () => undefined,
              })}

            {showFrontlayer &&
              renderGroup({
                label: "Start & navigation",
                icon: <GuestTvIcon />,
                testId: "root-nav-frontlayer",
                children: areaItems("/frontlayer", FRONTLAYER_SECTIONS),
                // Clicking the group opens Home screen, where the work starts.
                href: "/frontlayer/home",
                open: openAreas.has("frontlayer"),
                toggle: () => toggleArea("frontlayer"),
              })}

            {/* Guest web: its own entry, not a Start & navigation section
                (Sigge, 2026-09-29) - it is the guest's phone, not the TV. */}
            {showFrontlayer &&
              (hasPremise || customer) &&
              renderGroup({
                label: "Guest web",
                icon: <PhoneIcon />,
                testId: "root-nav-guest-web",
                children: [],
                href: "/guest-web",
                open: false,
                toggle: () => undefined,
              })}

            {showPromotions &&
              renderGroup({
                label: "Promotions",
                icon: <OffersIcon />,
                testId: "root-nav-promotions",
                children: areaItems("/promotions", PROMOTION_SECTIONS),
                open: openAreas.has("promotions"),
                toggle: () => toggleArea("promotions"),
              })}

            {showSignage &&
              renderGroup({
                label: "Signage",
                icon: <SignageIcon />,
                testId: "root-nav-signage",
                children: areaItems("/signage", SIGNAGE_SECTIONS),
                open: openAreas.has("signage"),
                toggle: () => toggleArea("signage"),
              })}

            {showVideoCalling &&
              renderGroup({
                label: "Video calling",
                icon: <VideoCallIcon />,
                testId: "root-nav-video-calling",
                // One section, so it is a link rather than a group of one.
                href: "/video-calling/video-calls",
                children: [],
                open: false,
                toggle: () => {},
              })}

            {showCare &&
              renderGroup({
                label: "Care",
                icon: <CareIcon />,
                testId: "root-nav-care",
                children: areaItems("/care", CARE_SECTIONS),
                open: openAreas.has("care"),
                toggle: () => toggleArea("care"),
              })}

            {renderGroup({
              label: "Analytics",
              icon: <InsightsIcon />,
              testId: "root-nav-analytics",
              // No href: it toggles, like every other group. Its own page
              // (Content analytics) is the first child instead.
              children: [
                {
                  // Chain-level: reads across companies, so it is offered even
                  // with no location picked.
                  label: "Content analytics",
                  href: "/content-analytics",
                  testId: "root-nav-content-analytics",
                  icon: <ContentAnalyticsIcon />,
                },
                ...(hasPremise ? areaItems("/places", ANALYTICS_SECTIONS) : []),
              ],
              open: openAreas.has("analytics"),
              toggle: () => toggleArea("analytics"),
              // One heading for everything below the products: analytics, the
              // platform library, users and settings are all Nenda-side tools
              // rather than things you do to a place. Splitting them across
              // "Insight" and "Admin" made two short lists where the reader
              // only ever wanted "the rest".
              sectionLabel: "Admin",
            })}

            {/* Distribution sits with the other Nenda-side tools, above the
                Administration catalogue: it is a top-level area of its own, not
                a setting. */}
            {renderGroup({
              label: "Distribution",
              icon: <DistributionIcon />,
              testId: "root-nav-distribution",
              children: distributionChildren,
              open: distributionOpen,
              toggle: () => setDistributionOpen((o) => !o),
            })}

            {secondaryItems.filter(canSee).map((it) => renderNavItem(it))}

            {renderGroup({
              label: "Administration",
              icon: <SettingsIcon />,
              testId: "root-nav-settings",
              children: settingsChildren,
              open: settingsOpen,
              toggle: () => setSettingsOpen((o) => !o),
              // No sectionLabel: this continues the Admin list opened above
              // rather than starting a second one.
            })}
          </List>
          <Divider />
          {/* The colour-mode toggle used to sit here as its own row. It moved
              into the account menu below: it is set once and then forgotten,
              so a permanent row for it cost more space than it was worth. */}
          <Box
            sx={{
              p: collapsed ? 1 : 1.5,
              ...(collapsed && { display: "flex", justifyContent: "center" }),
            }}
          >
            {collapsed ? (
              <Tooltip title={user?.user ?? "Account"} placement="right">
                <IconButton
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  aria-label="Account menu"
                  data-testid="root-user-menu"
                  sx={{ width: RAIL_ITEM, height: RAIL_ITEM }}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {userInitial}
                  </Avatar>
                </IconButton>
              </Tooltip>
            ) : (
              <Box
                onClick={(e) =>
                  setUserMenuAnchor(e.currentTarget as HTMLElement)
                }
                data-testid="root-user-menu"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                  {userInitial}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {user?.user ?? "-"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block" }}
                  >
                    {user?.type ?? ""}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <MenuItem disabled sx={{ opacity: "1 !important" }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user?.user}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Signed in as {user?.type}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            {/* Does NOT close the menu: switching theme is something you judge
                by looking, and a menu that vanishes on the first click makes
                changing your mind a second trip. */}
            <MenuItem onClick={toggleMode} data-testid="root-theme-toggle">
              <ListItemIcon sx={{ minWidth: 36 }}>
                {mode === "dark" ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </ListItemIcon>
              {mode === "dark" ? "Light mode" : "Dark mode"}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null);
                logout().then(() => navigate("/login", { replace: true }));
              }}
              data-testid="root-sign-out"
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Drawer>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <PageChromeCtx.Provider value={chrome}>
            <AppHeader
              slotRef={setHeaderSlot}
              claimed={headerClaims > 0}
              // Only on a handset: on desktop the drawer is permanent and a
              // button that opens an already-open thing is noise.
              onOpenNav={isHandset ? () => setNavOpen(true) : undefined}
            />
            {/* Keyed by path so the fade-in replays on every route change. */}
            <Box
              key={location.pathname}
              sx={{
                "@keyframes routeFadeIn": {
                  from: { opacity: 0, transform: "translateY(6px)" },
                  to: { opacity: 1, transform: "none" },
                },
                animation: "routeFadeIn 0.24s ease",
                // Anything too wide for the viewport scrolls HERE rather than
                // stretching the document. 16 of the 40 files that render a
                // table do so without a `TableContainer`, so on a narrow screen
                // they pushed the whole page sideways - the header, the nav
                // backdrop and every other page with it - which reads as
                // "broken" far more than a wide table does.
                //
                // Deliberately on this inner box and not the column outside it:
                // an overflow container becomes the scroll parent for
                // `position: sticky`, and the app header lives in that outer
                // column. Putting it here leaves the header pinned and lets the
                // page content scroll under it.
                overflowX: "auto",
              }}
            >
              <Outlet />
            </Box>
          </PageChromeCtx.Provider>
        </Box>
        <CommandPalette />
      </Box>
    </>
  );
}

export { WIDTH_OPEN, WIDTH_COLLAPSED };
